import { useRecoilValue } from 'recoil';
import { Organization } from 'generated-graphql/types';
import { useGetIntegrationCredentialsByAtsInstanceIdQuery } from 'generated-graphql/hooks/apollo-hooks';
import { organizationAtom } from 'services/organization/atom';
import { organizationGroupOrganizationsAtom } from 'services/organizationGroupOrganizations/atom';

export type AtsOrganizationResolution =
  | { status: 'loading' }
  | { status: 'resolved'; organization: Organization }
  | { status: 'notConnected' }
  | { status: 'unavailable' }
  | { status: 'failed' };

export const useResolveAtsOrganization = (
  instanceId: string,
  linkedOrganizationId: string | null
): AtsOrganizationResolution => {
  const organizations = useRecoilValue(organizationGroupOrganizationsAtom);
  const currentOrganization = useRecoilValue(organizationAtom);
  const { data, loading, error } =
    useGetIntegrationCredentialsByAtsInstanceIdQuery({
      variables: { atsInstanceId: instanceId }
    });

  if (loading || organizations === null) return { status: 'loading' };
  if (error) return { status: 'failed' };

  // The groups auth rule returns only rows of organisations the user belongs to.
  const connectedOrganizationIds = (
    data?.getIntegrationCredentialsByAtsInstanceId?.items ?? []
  ).flatMap((connectedCredentials) =>
    connectedCredentials && connectedCredentials.isActive !== false
      ? [connectedCredentials.organizationId]
      : []
  );

  if (connectedOrganizationIds.length === 0) return { status: 'notConnected' };

  const visibleConnectedOrganizations = connectedOrganizationIds.flatMap(
    (organizationId) => {
      const organization = organizations.find(
        (candidateOrganization) => candidateOrganization.id === organizationId
      );
      return organization ? [organization] : [];
    }
  );
  const organization =
    [linkedOrganizationId, currentOrganization?.id]
      .map((organizationId) =>
        visibleConnectedOrganizations.find(
          (candidateOrganization) => candidateOrganization.id === organizationId
        )
      )
      .find(Boolean) ?? visibleConnectedOrganizations[0];

  return organization
    ? { status: 'resolved', organization }
    : { status: 'unavailable' };
};
